<template>
  <button
    :type="nativeType"
    :disabled="disabled"
    :class="buttonClass"
    :style="buttonStyle"
    @click="onClick"
  >
    <Row :class="contentClass" x="c" y="c">
      <span v-if="$slots.prefix" class="inline-flex shrink-0 items-center justify-center text-current">
        <slot name="prefix" />
      </span>
      <span v-if="$slots.default" class="leading-none">
        <slot />
      </span>
      <span v-if="$slots.suffix" class="inline-flex shrink-0 items-center justify-center text-current">
        <slot name="suffix" />
      </span>
    </Row>
  </button>
</template>

<script setup>
const emit = defineEmits(['click'])

const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg'].includes(value),
  },
  variant: {
    type: String,
    default: 'primary',
    validator: value => ['primary', 'warning'].includes(value),
  },
  width: {
    type: [Number, String],
    default: undefined,
  },
  height: {
    type: [Number, String],
    default: undefined,
  },
  fontSize: {
    type: [Number, String],
    default: undefined,
  },
  fontWeight: {
    type: [Number, String],
    default: undefined,
  },
  nativeType: {
    type: String,
    default: 'button',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  block: {
    type: Boolean,
    default: false,
  },
  stopPropagation: {
    type: Boolean,
    default: false,
  },
  disableHoverColor: {
    type: Boolean,
    default: false,
  },
})

const sizeClassMap = {
  sm: 'h-32px px-14px text-14px weight-600',
  md: 'h-48px px-28px text-16px weight-600',
  lg: 'h-64px px-40px text-18px weight-600',
}

const contentClassMap = {
  sm: 'w-100b gap-6px',
  md: 'w-100b gap-8px',
  lg: 'w-100b gap-10px',
}

const variantClassMap = {
  primary: 'bg-primary text-white',
  warning: 'bg-warning text-16151A hover:bg-warning-hover!',
}

const buttonClass = computed(() => {
  return [
    'inline-flex items-center justify-center border-none rounded-999px cursor-pointer select-none appearance-none outline-none transition-colors-200ms disabled:cursor-not-allowed disabled:opacity-60',
    variantClassMap[props.variant],
    props.disableHoverColor || props.variant === 'warning' ? '' : 'hover:bg-1F4AFF!',
    sizeClassMap[props.size],
    props.block ? 'w-100b' : '',
  ]
})

const contentClass = computed(() => {
  return contentClassMap[props.size]
})

const toCssUnit = (value) => {
  if (value === undefined || value === null || value === '') {
    return undefined
  }

  return typeof value === 'number' ? `${value}px` : value
}

const buttonStyle = computed(() => {
  return {
    width: toCssUnit(props.width),
    height: toCssUnit(props.height),
    fontSize: toCssUnit(props.fontSize),
    fontWeight: props.fontWeight,
  }
})

const onClick = (event) => {
  if (props.stopPropagation) {
    event.stopPropagation()
  }

  emit('click', event)
}
</script>
